/**
 * ItemReplacementModal
 * 
 * Modal for searching and replacing items when creating a document.
 * Used when the assistant suggests a product that needs clarification or adjustment.
 */
import { useState, useMemo } from 'react';
import { RivhitItem } from '../../context/RivhitDataContext';

interface ItemReplacementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: RivhitItem) => void;
  items: RivhitItem[];
  currentItemName?: string;
}

export default function ItemReplacementModal({
  isOpen,
  onClose,
  onSelect,
  items,
  currentItemName,
}: ItemReplacementModalProps) {
  const [query, setQuery] = useState('');

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.slice(0, 20);
    return items
      .filter((item) =>
        [item.item_name, item.catalog_number, item.item_group_name, item.barcode]
          .filter(Boolean)
          .some((field) => field!.toLowerCase().includes(q))
      )
      .slice(0, 50);
  }, [query, items]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex h-full max-h-[600px] w-full max-w-lg flex-col overflow-hidden bg-white shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-100 px-6 py-4">
          <h3 className="text-lg font-bold text-gray-900">
            {currentItemName ? 'Replace Item' : 'Add Item'}
          </h3>
          {currentItemName && (
            <p className="text-sm text-gray-500">
              Replacing: <span className="font-medium text-gray-700">{currentItemName}</span>
            </p>
          )}
        </div>

        {/* Search */}
        <div className="border-b border-gray-50 bg-gray-50/50 px-6 py-4">
          <div className="relative">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, catalog #, or group..."
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 pl-10 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              🔍
            </span>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-2 scrollbar-thin">
          {filteredItems.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-gray-400">No matching items found</p>
            </div>
          ) : (
            <div className="grid gap-1">
              {filteredItems.map((item) => (
                <button
                  key={item.item_id ?? item.catalog_number ?? item.item_name}
                  onClick={() => {
                    onSelect(item);
                    onClose();
                  }}
                  className="group flex w-full items-center justify-between gap-4 rounded-xl p-3 text-left transition hover:bg-blue-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-blue-700">
                      {item.item_name}
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-0.5">
                      {item.catalog_number && (
                        <span className="text-[11px] font-mono text-gray-500">
                          #{item.catalog_number}
                        </span>
                      )}
                      {item.item_group_name && (
                        <span className="text-[11px] text-gray-400 italic">
                          {item.item_group_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    {item.price != null && (
                      <p className="text-sm font-bold text-gray-900">₪{Number(item.price).toFixed(2)}</p>
                    )}
                    <p className="text-[10px] text-gray-400 uppercase font-medium tracking-wider">
                      {item.quantity_type || 'Unit'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
